import { motion } from 'framer-motion';
import { AlertTriangle, TrendingUp, ChevronRight } from 'lucide-react';
import type { WeakArea } from '@/hooks/useProgress';

interface WeakAreasCardProps {
  weakAreas: WeakArea[];
  onPractice: () => void;
}

function AccuracyBar({ accuracy }: { accuracy: number }) {
  const color = accuracy >= 80 ? 'bg-success' : accuracy >= 60 ? 'bg-accent' : 'bg-destructive';
  return (
    <div className="w-full h-2 bg-white/5 border border-white/10 rounded-full overflow-hidden">
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={{ width: `${accuracy}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      />
    </div>
  );
}


export function WeakAreasCard({ weakAreas, onPractice }: WeakAreasCardProps) {
  if (weakAreas.length === 0) return null;

  const needsWork = weakAreas.filter(a => a.accuracy < 80);
  const strengths = weakAreas.filter(a => a.accuracy >= 80);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5 shadow-elevated space-y-4"
    >

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          <h3 className="font-display font-bold text-base">Your Strengths & Gaps</h3>
        </div>
      </div>

      {/* Weak areas */}
      {needsWork.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-display font-bold text-destructive flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Needs practice
          </p>
          {needsWork.slice(0, 3).map(area => (
            <div key={area.type} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-body flex items-center gap-1.5">
                  <span>{area.emoji}</span>
                  {area.label}
                </span>
                <span className={`text-xs font-display font-bold ${
                  area.accuracy < 50 ? 'text-destructive' : 'text-accent'
                }`}>
                  {area.accuracy}%
                </span>
              </div>
              <AccuracyBar accuracy={area.accuracy} />
            </div>
          ))}
        </div>
      )}

      {/* Strengths */}
      {strengths.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-display font-bold text-success flex items-center gap-1">
            ✅ Strong areas
          </p>
          {strengths.slice(0, 2).map(area => (
            <div key={area.type} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-body flex items-center gap-1.5">
                  <span>{area.emoji}</span>
                  {area.label}
                </span>
                <span className="text-xs font-display font-bold text-success">
                  {area.accuracy}%
                </span>
              </div>
              <AccuracyBar accuracy={area.accuracy} />
            </div>
          ))}
        </div>
      )}

      {/* Practice button */}
      {needsWork.length > 0 && (
        <button
          onClick={onPractice}
          className="w-full flex items-center justify-between p-3 rounded-xl bg-primary/10 border border-primary/30 hover:bg-primary/15 hover:border-primary/50 transition-all shadow-glow-primary"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🧠</span>
            <div className="text-left">
              <p className="font-display font-bold text-sm">Smart Review</p>
              <p className="text-xs text-foreground/60">Practice your weak areas</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-foreground/60" />
        </button>

      )}
    </motion.div>
  );
}
